import { AttractionTypesList } from '@/components/attraction-types';
import { AttractionThemesList } from '@/components/attractions/themes/AttractionThemesList';
import { AttractionTranslationsList } from '@/components/attractions/translations';
import { GalleryList } from '@/components/gallery';
import { ToursList } from '@/components/tours';
import { attractionTypeCrudConfig, type AttractionType } from '@/config/objects/attraction-types';
import { attractionCrudConfig, type Attraction } from '@/config/objects/attractions';
import { tourCrudConfig, type Tour } from '@/config/objects/tours';
import {
  CrudDetailView,
  PageContainer,
  useApiClient,
  type CrudConfig,
  type CrudDetailAction,
} from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  GenerateDescriptionsAction,
  GenerateDescriptionsIcon,
  GenerateVoiceoversAction,
  GenerateVoiceoversIcon,
} from './actions';

/**
 * Attraction detail page
 * Legacy: /Users/Thomas/projects/destinationtours/app/client/src/views/content/attractions/Attraction.jsx:81-224
 */
export function AttractionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [attraction, setAttraction] = React.useState<Attraction | null>(null);

  // Modal states
  const [generateDescriptionsModalOpen, setGenerateDescriptionsModalOpen] = useState(false);
  const [generateVoiceoversModalOpen, setGenerateVoiceoversModalOpen] = useState(false);

  /**
   * Delete attraction mutation
   */
  const deleteMutation = useMutation({
    mutationFn: async (attractionId: string): Promise<{ success: boolean; error?: string }> => {
      return apiClient.delete(`/attractions/${attractionId}`);
    },
    onSuccess: (response: { success: boolean; error?: string }) => {
      if (response.success) {
        // Invalidate attractions list to refresh data
        queryClient.invalidateQueries({ queryKey: ['Attraction'] });
        navigate('/attractions');
      } else {
        throw new Error(response.error || 'Failed to delete attraction');
      }
    },
  });

  // Create custom config for attraction-specific attraction types
  const attractionAttractionTypesConfig: CrudConfig<AttractionType> = useMemo(
    () => ({
      ...attractionTypeCrudConfig,
      entityName: 'Attraction Type',
      entityNamePlural: 'Attraction Types',
      endpoints: {
        ...attractionTypeCrudConfig.endpoints,
        list: `/attractions/${id}/attraction-types`,
        delete: (attractionTypeId: string) =>
          `/attractions/${id}/attraction-types/${attractionTypeId}`,
      },
      listView: {
        ...attractionTypeCrudConfig.listView,
        actions: {
          add: {
            enabled: true,
            endpoint: '/attraction-types',
            where: attraction
              ? {
                  attractions: {
                    none: {
                      attractionId: attraction.id,
                    },
                  },
                }
              : undefined,
            onAdd: async (attractionType: AttractionType) => {
              await apiClient.post(`/attractions/${id}/attraction-types`, {
                attractionTypeId: attractionType.id,
              });
              toast.success('Attraction type added to attraction');
            },
          },
          view: {
            enabled: true,
          },
          remove: {
            enabled: true,
            onRemove: async (attractionType: AttractionType) => {
              await apiClient.delete(`/attractions/${id}/attraction-types/${attractionType.id}`);
              toast.success('Attraction type removed from attraction');
            },
          },
        },
      },
    }),
    [attraction, id, apiClient],
  );

  // Create custom config for attraction-specific tours
  const attractionToursConfig: CrudConfig<Tour> = useMemo(
    () => ({
      ...tourCrudConfig,
      entityName: 'Tour',
      entityNamePlural: 'Tours',
      endpoints: {
        ...tourCrudConfig.endpoints,
        list: `/attractions/${id}/tours`,
        delete: (tourId: string) => `/attractions/${id}/tours/${tourId}`,
      },
      listView: {
        ...tourCrudConfig.listView,
        actions: {
          add: {
            enabled: true,
            endpoint: '/tours',
            where: attraction
              ? {
                  greaterAreaId: attraction.greaterAreaId,
                  attractions: {
                    none: {
                      attractionId: attraction.id,
                    },
                  },
                }
              : undefined,
            onAdd: async (tour: Tour) => {
              await apiClient.post(`/attractions/${id}/tours`, { tourId: tour.id });
              toast.success('Tour added to attraction');
            },
          },
          view: {
            enabled: true,
          },
          remove: {
            enabled: true,
            onRemove: async (tour: Tour) => {
              await apiClient.delete(`/attractions/${id}/tours/${tour.id}`);
              toast.success('Tour removed from attraction');
            },
          },
        },
      },
    }),
    [attraction, id, apiClient],
  );

  // Define custom detail actions
  const customActions: CrudDetailAction[] = useMemo(
    () => [
      {
        key: 'generate-descriptions',
        label: 'Generate Descriptions',
        icon: GenerateDescriptionsIcon,
        variant: 'secondary',
        onClick: () => setGenerateDescriptionsModalOpen(true),
      },
      {
        key: 'generate-voiceovers',
        label: 'Generate Voiceovers',
        icon: GenerateVoiceoversIcon,
        variant: 'secondary',
        onClick: () => setGenerateVoiceoversModalOpen(true),
      },
    ],
    [],
  );

  if (!id) {
    return (
      <PageContainer>
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Attraction Not Found</h2>
          <p className="text-muted-foreground">The requested attraction could not be found.</p>
        </div>
      </PageContainer>
    );
  }

  /**
   * Navigate to edit form for this attraction
   */
  const handleEdit = (attraction: Attraction) => {
    navigate(`/attractions/${attraction.id}/edit`);
  };

  /**
   * Handle attraction deletion
   */
  const handleDelete = async (attraction: Attraction) => {
    return toast.promise(deleteMutation.mutateAsync(attraction.id), {
      loading: 'Deleting attraction...',
      success: 'Attraction deleted successfully',
      error: (error: Error) => error?.message || 'Failed to delete attraction',
    });
  };

  const breadcrumbs = [
    { label: 'Home', href: '/' },
    { label: 'Attractions', href: '/attractions' },
    { label: attraction?.name || 'Attraction', current: true },
  ];

  return (
    <PageContainer>
      <div className="space-y-6">
        <CrudDetailView
          config={attractionCrudConfig}
          itemId={id}
          breadcrumbs={breadcrumbs}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onData={(attractionData: Attraction) => setAttraction(attractionData)}
          customActions={customActions}
        />

        {/* Attraction Types Section */}
        <AttractionTypesList
          config={attractionAttractionTypesConfig}
          className="p-0"
          baseUrl="/attraction-types"
        />

        {/* Tours Section */}
        <ToursList config={attractionToursConfig} className="p-0" baseUrl="/tours" />

        {/* Themes Section */}
        {id && <AttractionThemesList attractionId={id} className="p-0" />}

        {/* Translations Section */}
        {id && <AttractionTranslationsList attractionId={id} className="p-0" />}

        {/* Gallery Section */}
        {id && (
          <GalleryList
            attractionId={id}
            featuredImageId={attraction?.featuredImageId}
            onFeaturedImageSwap={() => {
              // The GalleryList already removes queries, but we can force a fresh attraction state
              setAttraction(null);
            }}
          />
        )}

        {/* Action Modals */}
        <GenerateDescriptionsAction
          attractionId={id}
          open={generateDescriptionsModalOpen}
          onOpenChange={setGenerateDescriptionsModalOpen}
        />

        <GenerateVoiceoversAction
          attractionId={id}
          open={generateVoiceoversModalOpen}
          onOpenChange={setGenerateVoiceoversModalOpen}
        />
      </div>
    </PageContainer>
  );
}
